import { useState, useRef, useEffect } from "react";
import {
  ArrowsOutSimple,
  Trash,
  TextAlignLeft,
  DotsSixVertical,
} from "@phosphor-icons/react";
import OptionPopover from "./OptionPopover";
import "../NivTasksPage.css";

const FALLBACK_OPTION = { label: "—", color: "#c4c4c4" };

/**
 * Single task row: inline title edit + colored option cells + actions.
 *
 * @param {Object}   task
 * @param {Object}   config      - tasks board config (options)
 * @param {function} onUpdate    - (taskId, data) => void
 * @param {function} onDelete    - (task) => void (page shows confirm)
 * @param {function} onOpenModal - (task) => void
 * @param {number}   index       - row index within the visible list
 * @param {string}   listId      - owning list id
 * @param {Object}   dnd         - drag&drop context from the page
 */
function TaskRow({ task, config, onUpdate, onDelete, onOpenModal, index, listId, dnd }) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [openPicker, setOpenPicker] = useState(null); // { field, anchor } | null
  const titleInputRef = useRef(null);

  const dndEnabled = dnd?.enabled === true;
  const isDragging = dndEnabled && dnd.draggedTaskId === task.id;
  const showDropBefore = dndEnabled && dnd.dropIndex === index;

  const handleRowDragOver = (e) => {
    if (!dndEnabled || !dnd.draggedTaskId) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const isAfter = e.clientY - rect.top > rect.height / 2;
    dnd.onDragOver(listId, index + (isAfter ? 1 : 0));
  };

  const handleRowDrop = (e) => {
    if (!dndEnabled || !dnd.draggedTaskId) return;
    e.preventDefault();
    e.stopPropagation();
    dnd.onDrop();
  };

  useEffect(() => {
    if (isEditingTitle) titleInputRef.current?.select();
  }, [isEditingTitle]);

  const startTitleEdit = () => {
    setTitleDraft(task.title);
    setIsEditingTitle(true);
  };

  const commitTitle = () => {
    setIsEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== task.title) {
      onUpdate(task.id, { title: trimmed });
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") commitTitle();
    if (e.key === "Escape") setIsEditingTitle(false);
  };

  const getOption = (field) => {
    const options = config?.options?.[field] || [];
    const found = options.find((o) => o.id === task[field]);
    if (found) return found;
    if (!task[field]) return FALLBACK_OPTION;
    return { label: task[field], color: "#c4c4c4" };
  };

  const renderOptionCell = (field, allowClear) => {
    const option = getOption(field);
    return (
      <div className={`nivtasks-cell nivtasks-cell-${field}`}>
        <button
          type="button"
          className="nivtasks-pill"
          style={{ backgroundColor: option.color }}
          onClick={(e) =>
            setOpenPicker(
              openPicker?.field === field ? null : { field, anchor: e.currentTarget }
            )
          }
        >
          {option.label}
        </button>
        {openPicker?.field === field && (
          <OptionPopover
            options={config?.options?.[field] || []}
            value={task[field]}
            anchorEl={openPicker.anchor}
            title={field.charAt(0).toUpperCase() + field.slice(1)}
            allowClear={allowClear}
            onSelect={(optionId) => onUpdate(task.id, { [field]: optionId })}
            onClose={() => setOpenPicker(null)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {showDropBefore && <div className="nivtasks-drop-line" />}
      <div
        className={`nivtasks-row ${isDragging ? "is-dragging" : ""}`}
        onDragOver={handleRowDragOver}
        onDrop={handleRowDrop}
      >
      <div className="nivtasks-cell nivtasks-cell-drag">
        {dndEnabled && (
          <span
            className="nivtasks-drag-handle"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              // Firefox requires data to be set for drag to start
              e.dataTransfer.setData("text/plain", task.id);
              dnd.onDragStart(task.id);
            }}
            onDragEnd={() => dnd.onDragEnd()}
            title="Drag to reorder"
            aria-label="Drag to reorder"
          >
            <DotsSixVertical size={16} weight="bold" />
          </span>
        )}
      </div>
      <div className="nivtasks-cell nivtasks-cell-title">
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            className="nivtasks-title-input"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={handleTitleKeyDown}
          />
        ) : (
          <button type="button" className="nivtasks-title-text" onClick={startTitleEdit} title="Click to edit">
            <span className="nivtasks-title-label">{task.title}</span>
            {task.description && <TextAlignLeft size={14} className="nivtasks-desc-indicator" />}
          </button>
        )}
      </div>

      {renderOptionCell("status", false)}
      {renderOptionCell("priority", false)}
      {renderOptionCell("type", true)}

      <div className="nivtasks-cell nivtasks-cell-actions">
        <button
          type="button"
          className="nivtasks-icon-btn"
          onClick={() => onOpenModal(task)}
          title="Open task"
          aria-label="Open task"
        >
          <ArrowsOutSimple size={16} />
        </button>
        <button
          type="button"
          className="nivtasks-icon-btn nivtasks-icon-btn-danger"
          onClick={() => onDelete(task)}
          title="Delete task"
          aria-label="Delete task"
        >
          <Trash size={16} />
        </button>
      </div>
      </div>
    </>
  );
}

export default TaskRow;
